import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/Api';
import { useSelector } from 'react-redux';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import UserAvatar from '../../components/UserAvatar';
import { formatDate } from '../../utils/Date';
import { BeatLoader } from 'react-spinners';
import { FaTrash, FaChevronDown, FaChevronUp, FaRegCommentDots } from 'react-icons/fa';
import { MdOutlineComment } from 'react-icons/md';

const ViewBlog = () => {
  const { userData } = useSelector(state => state.auth);
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [activeReplyId, setActiveReplyId] = useState(null);
  const [commentsCollapsed, setCommentsCollapsed] = useState(false);
  const [collapsedComments, setCollapsedComments] = useState({});
  const [collapsedReplies, setCollapsedReplies] = useState({});
  const commentFormRef = useRef(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/blog/id/${id}`);
        setBlog(response?.data?.blog);
      } catch (err) {
        setError(err.message || 'Failed to fetch blog.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBlog();
    }
  }, [id]);

  const fetchRepliesForComment = async commentId => {
    try {
      const repliesResponse = await api.get(`/blog/by-any/blogId/${commentId}`);
      return repliesResponse?.data?.data || [];
    } catch (err) {
      console.error('Failed to fetch replies:', err);
      if (err.response && err.response.status === 404) {
        return [];
      }
      return [];
    }
  };

  const fetchComments = async () => {
    try {
      const commentsResponse = await api.get(`/blog/by-any/blogId/${id}`);
      const commentsData = commentsResponse?.data?.data || [];

      const commentsWithReplies = await Promise.all(
        commentsData.map(async comment => {
          const replies = await fetchRepliesForComment(comment._id);
          return { ...comment, replies };
        })
      );

      setComments(commentsWithReplies);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      if (err.response && err.response.status === 404) {
        setComments([]);
      } else {
        console.error(`Error ${err.response?.status}: ${err.message}`);
        setComments([]);
      }
    }
  };

  useEffect(() => {
    if (id) {
      fetchComments();
    }
  }, [id]);

  useEffect(() => {
    if (showCommentForm && commentFormRef.current) {
      setTimeout(() => {
        commentFormRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [showCommentForm]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCommentSubmit = async e => {
    e.preventDefault();
    if (!commentText.trim()) return;

    try {
      setSubmitting(true);
      const adminId = userData?.sub;

      if (replyingTo) {
        await api.post(`/blog`, {
          adminId,
          blogId: replyingTo,
          content: commentText,
        });
      } else {
        await api.post(`/blog`, {
          adminId,
          blogId: id,
          content: commentText,
        });
      }

      await fetchComments();

      setCommentText('');
      setReplyingTo(null);
      setActiveReplyId(null);
      setShowCommentForm(false);
    } catch (err) {
      setError(err.message || 'Failed to post comment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyClick = commentId => {
    setReplyingTo(commentId);
    setActiveReplyId(commentId);
    setCommentText('');
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setActiveReplyId(null);
    setCommentText('');
  };

  const handleDeleteComment = async commentId => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await api.delete(`/blog/${commentId}`);
        fetchComments();
      } catch (err) {
        setError(err.message || 'Failed to delete comment.');
      }
    }
  };

  const handleDeleteReply = async (replyId, commentId) => {
    if (window.confirm('Are you sure you want to delete this reply?')) {
      try {
        await api.delete(`/blog/${replyId}`);
        fetchComments();
      } catch (err) {
        setError(err.message || 'Failed to delete reply.');
      }
    }
  };

  const toggleCommentForm = () => {
    setShowCommentForm(!showCommentForm);
  };

  const toggleCommentsSection = () => {
    setCommentsCollapsed(!commentsCollapsed);
  };

  const toggleCommentCollapse = commentId => {
    setCollapsedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const toggleReplyCollapse = commentId => {
    setCollapsedReplies(prev => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  if (loading) {
    return (
      <div className="w-full p-4 pl-64 h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} data-testid="beat-loader" />
      </div>
    );
  }

  if (error && !blog) {
    return (
      <div className="w-full px-5 lg:p-5">
        <div
          className="flex items-center p-4 mb-4 text-red-800 border-t-4 border-red-300 bg-red-50"
          role="alert">
          <div className="text-xs font-light ms-3">{error}</div>
          <button
            type="button"
            className="ms-auto -mx-1.5 -my-1.5 bg-red-50 text-red-500 rounded-lg focus:ring-2 focus:ring-red-400 p-1.5 hover:bg-red-200 inline-flex items-center justify-center h-8 w-8"
            onClick={() => setError(null)}
            aria-label="Close">
            <span className="sr-only">Dismiss</span>
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen px-5 lg:p-5">
        <h2 className="text-2xl font-light text-gray-800">Blog not found</h2>
        <button
          onClick={handleBack}
          className="px-6 py-2 mt-4 text-white transition duration-200 bg-black rounded hover:bg-gray-800">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:ml-64 bg-[#F4F5F6] w-[80%] font-nohemi">
      <div className="p-8 bg-white">
        <div className="relative pt-4">
          <div className="bg-[#F5F5F5] rounded-lg p-4 pb-72">
            <h1 className="mb-4 text-base font-normal text-gray-900">{blog.title}</h1>
          </div>

          <div className="absolute left-0 right-0 px-4 top-20">
            <img
              src={blog.imageUrl}
              alt="Scenic view"
              className="w-[98%] h-96 object-cover rounded-lg mx-auto"
            />
            <div className="flex items-center justify-center gap-6 mt-4 text-sm text-gray-500">
              <span>Created at: {formatDate(blog?.createdAt)}</span>
              <span>Comments: {comments?.length || 0}</span>
              <span>Category: {blog?.tags.map(tag => tag).join(', ')}</span>
            </div>
          </div>

          <div className="p-6 rounded-lg mt-28">
            <div className="p-3 mt-4 space-y-4 text-gray-800">
              <h2 className="text-base font-bold">
                {blog.description || 'No description available'}
              </h2>
              <div
                className="prose prose-lg text-gray-700 max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-li:marker:text-gray-400"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(blog.content) }}
              />
            </div>

            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <button onClick={toggleCommentsSection} className="flex items-center gap-2">
                  {commentsCollapsed ? <FaChevronDown size={12} /> : <FaChevronUp size={12} />}
                  <h2 className="text-sm font-semibold">Comments ({comments?.length})</h2>
                </button>
                <button
                  onClick={toggleCommentForm}
                  className="bg-black text-white px-3 py-4 rounded-lg text-sm hover:bg-gray-800 transition duration-200">
                  Comment on post
                </button>
              </div>

              {!commentsCollapsed && (
                <>
                  {showCommentForm && (
                    <div className="p-4 mb-8 rounded-lg bg-gray-50" ref={commentFormRef}>
                      <h2 className="mb-4 text-xs font-semibold">
                        {replyingTo ? 'Reply to Comment' : 'Add a Comment'}
                      </h2>

                      {replyingTo && (
                        <div className="flex items-center justify-between p-2 mb-3 text-sm bg-gray-100 rounded-lg">
                          <span>Replying to comment</span>
                          <button
                            onClick={handleCancelReply}
                            className="text-gray-500 hover:text-gray-700">
                            Cancel
                          </button>
                        </div>
                      )}

                      <form onSubmit={handleCommentSubmit} className="space-y-4">
                        <div>
                          <textarea
                            className="w-full p-3 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                            rows="4"
                            placeholder="Write your comment here..."
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            required
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowCommentForm(false);
                              setReplyingTo(null);
                              setCommentText('');
                            }}
                            className="px-4 py-2 text-xs text-gray-800 transition duration-200 bg-gray-200 rounded-lg hover:bg-gray-300">
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 text-xs text-white transition duration-200 bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50">
                            {submitting ? 'Posting...' : replyingTo ? 'Post Reply' : 'Post Comment'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  <div className="space-y-4">
                    {comments.length > 0 ? (
                      comments.map((comment, index) => (
                        <div key={comment._id || index} className="mb-4">
                          {collapsedComments[comment._id] ? (
                            <div
                              onClick={() => toggleCommentCollapse(comment._id)}
                              className="flex items-center w-1/3 gap-2 p-2 transition-all bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
                              <MdOutlineComment size={16} className="text-gray-500" />
                              <span className="text-sm text-gray-500">
                                {comment?.userId?.firstName ||
                                  comment?.adminId?.fullName ||
                                  'Anonymous'}{' '}
                                {comment?.userId?.lastName} • {formatDate(comment?.createdAt)}
                                {comment?.replies?.length > 0 &&
                                  ` • ${comment.replies.length} ${
                                    comment.replies.length === 1 ? 'reply' : 'replies'
                                  }`}
                              </span>
                            </div>
                          ) : (
                            <div className="bg-[#F5F5F5] p-4 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <button
                                    onClick={() => toggleCommentCollapse(comment._id)}
                                    className="text-gray-500 hover:text-gray-700"
                                    title="Collapse comment">
                                    <FaChevronUp size={12} />
                                  </button>
                                  <UserAvatar
                                    user={comment?.userId || comment?.adminId}
                                    size="w-10 h-10"
                                    variant="xl"
                                  />
                                  <div>
                                    <p className="font-semibold text-[#3E3232] text-sm">
                                      {comment?.userId?.firstName ||
                                        comment?.adminId?.fullName ||
                                        'Anonymous'}{' '}
                                      {comment?.userId?.lastName}
                                    </p>
                                    <p className="text-xs text-gray-600">
                                      {formatDate(comment?.createdAt)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    className="bg-gray-200 py-1 px-3 text-[#3E3232] text-sm rounded-sm hover:bg-gray-300"
                                    onClick={() => handleReplyClick(comment?._id)}>
                                    Reply
                                  </button>
                                  {(userData?.sub === comment?.userId?._id ||
                                    userData?.sub === comment?.adminId?._id) && (
                                    <button
                                      onClick={() => handleDeleteComment(comment._id)}
                                      className="text-red-500 hover:text-red-700"
                                      title="Delete comment">
                                      <FaTrash size={12} />
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div className="mt-3">
                                <p className="text-sm text-[#3E3232]">{comment?.content}</p>
                              </div>

                              {activeReplyId === comment._id && (
                                <div className="p-3 mt-3 bg-white rounded-lg">
                                  <form onSubmit={handleCommentSubmit} className="space-y-3">
                                    <textarea
                                      className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                                      rows="3"
                                      placeholder="Write your reply here..."
                                      value={commentText}
                                      onChange={e => setCommentText(e.target.value)}
                                      required
                                    />
                                    <div className="flex justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={handleCancelReply}
                                        className="px-3 py-1 text-sm text-gray-800 transition duration-200 bg-gray-200 rounded-lg hover:bg-gray-300">
                                        Cancel
                                      </button>
                                      <button
                                        type="submit"
                                        disabled={submitting}
                                        className="px-3 py-1 text-sm text-white transition duration-200 bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50">
                                        {submitting ? 'Posting...' : 'Post Reply'}
                                      </button>
                                    </div>
                                  </form>
                                </div>
                              )}

                              {comment?.replies && comment?.replies.length > 0 && (
                                <div className="mt-4 ml-10 space-y-3">
                                  <button
                                    onClick={() => toggleReplyCollapse(comment._id)}
                                    className="flex items-center mb-2 text-sm text-gray-500">
                                    {collapsedReplies[comment._id] ? (
                                      <FaRegCommentDots size={14} className="mr-1" />
                                    ) : (
                                      <FaChevronUp size={12} className="mr-1" />
                                    )}
                                    {comment.replies.length}{' '}
                                    {comment.replies.length === 1 ? 'reply' : 'replies'}
                                  </button>

                                  {!collapsedReplies[comment._id] && (
                                    <div className="space-y-3">
                                      {comment.replies.map((reply, replyIndex) => (
                                        <div
                                          key={reply._id || replyIndex}
                                          className="p-3 bg-white rounded-lg">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                              <UserAvatar
                                                user={reply?.userId || reply?.adminId}
                                                size="w-8 h-8"
                                                variant="lg"
                                              />
                                              <div>
                                                <p className="font-semibold text-[#3E3232] text-sm">
                                                  {reply?.userId?.firstName ||
                                                    reply?.adminId?.fullName ||
                                                    'Anonymous'}{' '}
                                                  {reply?.userId?.lastName}
                                                </p>
                                                <p className="text-gray-600 text-sm">
                                                  {formatDate(reply?.createdAt)}
                                                </p>
                                              </div>
                                            </div>
                                            {(userData?.sub === reply?.userId?._id ||
                                              userData?.sub === reply?.adminId?._id) && (
                                              <button
                                                onClick={() =>
                                                  handleDeleteReply(reply._id, comment._id)
                                                }
                                                className="text-red-500 hover:text-red-700"
                                                title="Delete reply">
                                                <FaTrash size={10} />
                                              </button>
                                            )}
                                          </div>
                                          <p className="mt-2 text-sm text-[#3E3232]">
                                            {reply?.content}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="py-4 text-sm text-center text-gray-500">
                        No comments yet. Be the first to comment!
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBlog;
